import {
    Additional,
    AdditionalType,
    ApplicationScope,
    EnterprisePrice,
    GraduatedPrice,
    MyDbProductCategory,
    MyDbProductDetails,
    Price,
    Pricing,
    Product,
    ProductCategory,
    ProductCategoryFromJSON,
    ProductImageDetails,
    ProductType,
    PurchaseItemType,
    SpecificCustomer,
    SubscriptionDetails,
    ThresholdPrice,
    Unit,
    Customer, SpecificOrganisation, TenantProductDetails, TimeUnit, SpecificActiveProduct, Order
} from "~/api-client";
import { errorMsg, initializeProductsUtil } from "~/modules/common/services/util/util";
import {defineStore} from "pinia";
import { useOrderStore } from "~/modules/common/stores/orders";
import _ from "lodash";

export interface SellableItem {
    editItem: boolean,
    createItem: boolean ,
    purchaseItemType: PurchaseItemType,
    item: QualifiablePurchasable
}
export interface QualifiablePurchasable {
    purchasable: Purchasable;
    quantity: number;
    category: string;
}

export interface QualifiedPurchasable {
    purchasable: Purchasable;
    quantity: number;
}

export type ProductDetailsType = MyDbProductDetails;

export interface Purchasable {
    id: string;
    name: string;
    type: PurchaseItemType;

    description?: string;
    quantityDescription?: string;
    productImageId?: string;


    // products haben implizit LINEAR
    pricing?: Pricing;

    price?: Price;
    thresholdPrices?: Array<ThresholdPrice>;
    graduatedPrices?: Array<GraduatedPrice>;
    enterprisePrice?: EnterprisePrice;
    fixedQuantity?: boolean;
    orderNr?: number;
    storeAvailability?: boolean;

// nur bei product
    productCategory?: MyDbProductCategory;
    productType?: ProductType;
    canonicalId?: string;
    unit?: Unit;
    subscriptionDetails?: SubscriptionDetails;
    productsSize?: number;
    forSpecificCustomer?: boolean;
    availableToPublic?: boolean;
    specificCustomers?: SpecificCustomer[];
    specificOrganisations?: SpecificOrganisation[],
    specificActiveProducts?: SpecificActiveProduct[],
    tenantDetails?: ProductDetailsType;

// nur bei additional
    parentProductId?: string;
    parentProductAdditionalsSize?: number;
    additionalType?: AdditionalType;
}

interface State {
    _selectedProduct: string | null,
    _productById: { [id: string]: Product };
    _purchasableById: { [id: string]: Purchasable };
    _purchasablesByCategory: { [category: string]: Purchasable[] };
    _productsByCategory: { [category: string]: Product[] };
    _categories: string[];
    _categoryById: { [id: string]: ProductCategory };
    _allProductCategoriesFromCategories: ProductCategory[];
    _productCategoriesForTenant: any[];
    _allProducts: Product[];
    _allProductImages: ProductImageDetails[];
    _allNonSubscriptionPurchasable: { [category: string]: Purchasable[] }
}

export const useProductStore: any = defineStore('products', {
    state: (): State => ({
        _selectedProduct: null,
        _productById: {},
        _purchasableById: {},
        _purchasablesByCategory: {},
        _productsByCategory: {},
        _categories: [],
        _categoryById: {},
        _allProductCategoriesFromCategories: [],
        _productCategoriesForTenant: [],
        _allProducts: [],
        _allProductImages: [],
        _allNonSubscriptionPurchasable: {},
    }),

    getters: {
        selectedProduct: (state) => (state._selectedProduct),
        productById: (state) => (id: string) => (state._productById[id]),
        purchasableById: (state) => (id: string) => (state._purchasableById[id]),
        productsByCategory: (state) => (category: string) => (state._productsByCategory[category]),
        purchasablesByCategory: (state) => (category: string) => (state._purchasablesByCategory[category]),
        allCategories: (state) => (state._categories),
        categoryById: (state) => (id: string) => (state._categoryById[id]),
        allProductCategoriesFromCategories: (state) => (state._allProductCategoriesFromCategories),
        productCategoriesForTenant: (state) => (state._productCategoriesForTenant),
        cheapestProductByCategory: (state) => (category: string) => (!!state._productsByCategory[category] ? [...state._productsByCategory[category]].sort((a, b) => a.price!.net!! - b.price!.net!!)[0] : null),
        allProducts: (state) => (state._allProducts),
        allProductImages: (state) => (state._allProductImages),
        allowedExtraPurchases: (state) => {
            const {$customerStore, $subscriptionStore} = useNuxtApp();
            const allNSPurchasable = state._allNonSubscriptionPurchasable
            let allowedPurchasable: { [category: string]: Purchasable[] } = {};
            const setAllowedProductIdForUser: Set<string> = new Set([]);
            $customerStore.orderHistory.forEach((order: Order)=> {
                if (!!order.cart?.items){
                    for (const item of order.cart.items) {
                        if (order.subscription){
                            if ($subscriptionStore.currentUserActiveSubscription?.productId === item.productId && !item.additionalId){
                                setAllowedProductIdForUser.add(item.productId);
                            }
                        } else {
                            if (!item.additionalId){
                                setAllowedProductIdForUser.add(item.productId);
                            }
                        }
                    }
                }
            });
            for (const cat in allNSPurchasable){
                const purchasableList = allNSPurchasable[cat];
                const allowedList = purchasableList.filter((p: Purchasable) => !!p.storeAvailability
                  && (!p.specificCustomers || p.specificCustomers.length === 0 || p.specificCustomers.map(sp => sp.id).includes($customerStore.currentCustomer.id))
                  && (!p.specificOrganisations || p.specificOrganisations.length === 0 || p.specificOrganisations?.map(spO => spO.id).includes($customerStore.currentCustomer.organisation?.id))
                  && (!p.specificActiveProducts || p.specificActiveProducts.length === 0 || p.specificActiveProducts?.map(sp1 => sp1.id!).some(prodId => setAllowedProductIdForUser.has(prodId)))
                );
                if (allowedList.length > 0){
                    allowedPurchasable[cat] = allowedList;
                }
            }
            return allowedPurchasable;
        },
        allowedExtraPurchasesCategories(){
            return Object.keys(this.allowedExtraPurchases);
        },
        isAllowedExtraPurchases: function() {
            const { $subscriptionStore } = useNuxtApp();
            return !_.isEmpty(this.allowedExtraPurchases) && !!$subscriptionStore.currentUserActiveSubscription;
        }
    },

    actions: {
        // resetSelectedProduct(): void {
        //     this.$state._selectedProduct = null;
        // },
        updateSelectedProduct(product: string | null){
            this.$state._selectedProduct = product;
        },
        updateProductsFromBackend(): Promise<boolean> {
            const {$backendService, $logger, $tenant} = useNuxtApp();
            $logger.info('reloading products');
            return $backendService.productControllerApi.getProducts({xApplicationScope: $tenant.getTenant
            }).then((result: Product[]) => {
                this.settingProductsInStore(result);
                return true;
            }).catch((error) => {
                $logger.error('error fetching products', error);
                return false;
            });
        },
        settingProductsInStore(products: Product[]){
            const {$logger, $tenant} = useNuxtApp();

            const [productsByCategory, productById, purchasablesByCategory, purchasableById, categories, categoryById] =
                initializeProductsUtil(products)

            this.$state._productsByCategory = productsByCategory;
            this.$state._productById = productById;
            this.$state._purchasablesByCategory = purchasablesByCategory;
            this.$state._purchasableById = purchasableById;
            this.$state._categories = categories;
            this.$state._categoryById = categoryById;
            this.$state._allProductCategoriesFromCategories = categories.map((c: string) => {return ProductCategoryFromJSON(c)});
            this.$state._productCategoriesForTenant = this.getProductCategoriesForTenant($tenant.getTenant);
            this.$state._allProducts = products;
            this.$state._allNonSubscriptionPurchasable = this.getNonSubscriptionPurchasableByCategory(purchasablesByCategory);
            $logger.info('initialized products');
        },
        getNonSubscriptionPurchasableByCategory(purchasablesByCategory: {[category: string]: Purchasable[]}){
            let allNSPurchasable: { [category: string]: Purchasable[] } = {};
            for (const cat in purchasablesByCategory){
                const purchasableList = purchasablesByCategory[cat];
                const nonSubscriptionList = purchasableList.filter(p => !p.productCategory!.includes("SUBSCRIPTION"));
                if (nonSubscriptionList.length > 0){
                    allNSPurchasable[cat] = nonSubscriptionList;
                }
            }
            return allNSPurchasable;
        },
        getAllowedExtraPurchases(){

        },
        getPurchasablesByCategory(category: string): QualifiablePurchasable[] {
            return this.$state._purchasablesByCategory[category]?.map(purchasable => ({
                purchasable: purchasable,
                quantity: purchasable.fixedQuantity ? 1 : 0,
                category: category,
            }));
        },
        getProductCategoriesForTenant(tenant: ApplicationScope){
            switch(tenant){
                case "MY_DB": return Object.values(MyDbProductCategory);
                default: return Object.values(ProductCategory);
            }
        },
        changeStoreAvailability(row: QualifiablePurchasable): Promise<boolean>{
            const {$backendService, $logger, $tenant} = useNuxtApp();

            const currentMode = row.purchasable.storeAvailability;
            let backendCall = $backendService.productControllerApi.updateProduct({
                xApplicationScope: $tenant.getTenant,
                product: {id: row.purchasable.id, storeAvailability: row.purchasable.storeAvailability}
            });
            // switch (row.purchasable.type) {
            //   case "PRODUCT":
            //     backendCall = this.$backendService.productControllerApi.updateProduct({
            //       xApplicationScope: $tenant.getTenant,
            //       product: {id: row.purchasable.id, storeAvailability: row.purchasable.storeAvailability}
            //     })
            //     break;
            //   case "ADDITIONAL":
            //     backendCall = this.$backendService.productControllerApi.updateProduct({
            //       xApplicationScope: $tenant.getTenant,
            //       product: {
            //         id: row.purchasable.parentProductId,
            //         additionals: [{
            //           id: row.purchasable.id,
            //           ...this.getTenantDetailsFromPurchasable(row),
            //           storeAvailability: row.purchasable.storeAvailability
            //         }]
            //       }
            //     })
            //     break;
            // }
            return backendCall.then(() => {
                return true;
            }).catch((error) => {
                row.purchasable.storeAvailability = !currentMode;
                $logger.error('CHANGE_PRODUCT_DATA', error);
                return false;
            });
        },
        changeOrderProduct(direction: string, type: PurchaseItemType, row: QualifiablePurchasable, product: Product) : Promise<boolean> {
            const {$backendService, $logger, $tenant} = useNuxtApp();

            let backendCall;
            if (direction === "up") {
                backendCall =  $backendService.productControllerApi.increaseOrderProduct({
                    xApplicationScope: $tenant.getTenant,
                    product: product
                });
            } else {
                backendCall = $backendService.productControllerApi.decreaseOrderProduct({
                    xApplicationScope: $tenant.getTenant,
                    product: product
                });
            }
            // switch (type) {
            //     case "PRODUCT":
            //         break;
                    // switch (type) {
                    //   case 'PRODUCT':
                    //     if (direction === 'up') {
                    //       backendCall = this.$backendService.productControllerApi.increaseOrderProduct({
                    //         xApplicationScope: $tenant.getTenant,
                    //         product: {
                    //           id: row!.purchasable.id,
                    //           productCategory: row.purchasable.productCategory,
                    //           orderNr: row!.purchasable.orderNr - 1,
                    //           ...this.getTenantDetailsFromPurchasable(row)
                    //         }
                    //       });
                    //     } else {
                    //       backendCall = this.$backendService.productControllerApi.decreaseOrderProduct({
                    //         xApplicationScope: $tenant.getTenant,
                    //         product: {
                    //           id: row!.purchasable.id,
                    //           productCategory: row.purchasable.productCategory,
                    //           orderNr: row!.purchasable.orderNr + 1,
                    //           ...this.getTenantDetailsFromPurchasable(row)
                    //         }
                    //       });
                    //     }
                    //     break;

            //
            // }
            return backendCall
              .then(_ => {
                  return this.updateProductsFromBackend().then(state => {
                      return state;
                  });
                  // })
                  // .then(state => {
                  //     return state;
              }).catch(error => {
                $logger.error("CHANGE_PRODUCT_DATA", error);
                return false;
            });
        },
        changeOrderAdditional(direction: string, parentProductId: string, additional: Additional) : Promise<boolean> {
            const {$backendService, $logger, $tenant} = useNuxtApp();

            let backendCall;
            if (direction === 'up') {
                backendCall = $backendService.productControllerApi.increaseOrderAdditional({
                    xApplicationScope: $tenant.getTenant,
                    parentProductId: parentProductId,
                    additional: additional,
                });
            } else {
                backendCall = $backendService.productControllerApi.decreaseOrderAdditional({
                    xApplicationScope: $tenant.getTenant,
                    parentProductId: parentProductId,
                    additional: additional
                });
            }
            return backendCall
              .then(_ => {
                  return this.updateProductsFromBackend().then(state => {
                      return state;
                  });
                  // })
                  // .then(state => {
                  //     return state;
              }).catch(error => {
                $logger.error("CHANGE_PRODUCT_DATA", error);
                return false;
            });
        },

        // backendCallMethod(call: any, msgSuccess: string): Promise<void> {
        //     return call.then((_: any) => {
        //         ElMessage.success(msgSuccess);
        //         this.updateProductsFromBackend();
        //     }).catch((error: any) => {
        //         errorMsg(error).then(error_msg => ElMessage.error(error_msg));
        //     });
        // },
        createProduct(product: Product){
            const {$backendService, $logger, $tenant} = useNuxtApp();

            let msgSuccess: string = 'Das neue Produkt wurde erfolgreich erstellt';

            return $backendService.productControllerApi.createProduct({
                xApplicationScope: $tenant.getTenant, product: product
            }).then(result => {
                const newAllProducts: Product[] = [...this.$state._allProducts, result];
                this.settingProductsInStore(newAllProducts);
                ElMessage.success(msgSuccess);
                return result;
            }).catch(error => {
                errorMsg(error).then(error_msg => ElMessage.error(error_msg));
                throw error;
            });
        },
        updateProduct(product: Product){
            const {$backendService, $logger, $tenant} = useNuxtApp();

            let msgSuccess: string = 'Das Produkt wurde erfolgreich bearbeitet';

            return $backendService.productControllerApi.updateProduct({
                xApplicationScope: $tenant.getTenant,
                product: product
            }).then(result => {
                this.$state._allProducts = this.$state._allProducts.map(product => {
                    if (product.id === result.id){
                        return result;
                    }
                    return product;
                });
                this.settingProductsInStore(this.$state._allProducts);
                ElMessage.success(msgSuccess);
                return result;
            }).catch((error: any) => {
                errorMsg(error).then(error_msg => ElMessage.error(error_msg));
                throw error
            });
        },
        deleteProduct(product: Product){
            const {$backendService, $logger, $tenant} = useNuxtApp();

            let msgSuccess: string = 'Das Produkt wurde erfolgreich gelöscht';
            return $backendService.productControllerApi.deleteProduct({
                xApplicationScope: $tenant.getTenant,
                product: product
            }).then(_ => {
                const newAllProducts = this.$state._allProducts.reduce((filtered: Product[], prod: Product) => {
                    if (prod.id !== product.id) {
                        filtered.push(prod);
                    }
                    return filtered;
                }, []);
                this.settingProductsInStore(newAllProducts);
                ElMessage.success(msgSuccess);
                return;
            }).catch((error: any) => {
                errorMsg(error).then(error_msg => ElMessage.error(error_msg));
                throw error;
            });
        },
        deleteAdditional(product: Product){
            const {$backendService, $logger, $tenant} = useNuxtApp();

            let msgSuccess: string = 'Die Zusatzoption des Produkts wurde erfolgreich gelöscht';
            return $backendService.productControllerApi.deleteAdditional({
                xApplicationScope: $tenant.getTenant,
                product: product
            }).then(result => {
                this.$state._allProducts = this.$state._allProducts.map(product => {
                    if (product.id === result.id){
                        return result;
                    }
                    return product;
                })
                this.settingProductsInStore(this.$state._allProducts);
                ElMessage.success(msgSuccess);
                return result;
            }).catch((error: any) => {
                errorMsg(error).then(error_msg => ElMessage.error(error_msg));
                throw error
            });
        },
        getAllProductImages(){
            const {$backendService, $logger, $tenant} = useNuxtApp();

            return $backendService.productControllerApi.getAllProductImageDetailsByTenant({xApplicationScope: $tenant.getTenant})
                .then(result => {
                    this.$state._allProductImages = result
                    return result;
                }).catch(err => {
                    $logger.error("error updating product images");
                    ElMessage.error("Beim Hochladen des Bildes ist leider ein Fehler aufgetreten")
                    throw err;
                })
        },
        createProductImage(file: any, requestOptions: any){
            const {$backendService, $logger, $tenant} = useNuxtApp();

            return $backendService.productControllerApi.createProductImage({
                xApplicationScope: $tenant.getTenant,
                body: file.raw
            }, requestOptions)
              .then((result: ProductImageDetails) => {
                  result.imageName = file.name;
                  return this.updateProductImage(result);
              }).then((result: ProductImageDetails[]) => {
                this.$state._allProductImages = result;
            }).catch(err => {
                $logger.error("error updating image");
                ElMessage.error("Beim Hochladen des Bildes ist leider ein Fehler aufgetreten")
                throw err;
            })
        },
        updateProductImage(productImage: ProductImageDetails){
            const {$backendService, $logger, $tenant} = useNuxtApp();

            return $backendService.productControllerApi.updateProductImageDetails({
                xApplicationScope: $tenant.getTenant, productImageDetails: { id: productImage.id, imageName: productImage.imageName }
            }).then((result: ProductImageDetails[]) => {
                this.$state._allProductImages = result;
                return result;
            }).catch(err => {
                $logger.error("error uploading image");
                ElMessage.error("Beim Hochladen des Bildes ist leider ein Fehler aufgetreten")
                throw err;
            })
        },
        deleteProductImageById(imageId: string){
            const {$backendService, $logger, $tenant} = useNuxtApp();

            return $backendService.productControllerApi.deleteProductImageById({
                xApplicationScope: $tenant.getTenant,
                id: imageId
            }).then((result: ProductImageDetails[]) => {
                this.$state._allProductImages = result;
                return result;
            }).catch(err => {
                $logger.error("error deleting image with id {}", imageId);
                ElMessage.error("Beim Löschen des Bildes ist leider ein Fehler aufgetreten")
                throw err;
            })
        }
    }
})


function buildPurchasableFromProduct(element: Product): Purchasable {
    const {$tenant} = useNuxtApp();
    return {
        id: element.id!,
        name: element.name!,
        canonicalId: element.canonicalId,
        unit: element.unit,
        type: PurchaseItemType.Product,
        description: element.description,
        quantityDescription: element.quantityDescription,
        pricing: element.pricing,
        price: element.price,
        graduatedPrices: element.graduatedPrices,
        thresholdPrices: element.thresholdPrices,
        enterprisePrice: element.enterprisePrice,
        productCategory: (<MyDbProductCategory>element.productCategory),
        productType: element.productType,
        fixedQuantity: element.fixedQuantity,
        availableToPublic: element.availableToPublic,
        specificCustomers: !!element.specificCustomers ? element.specificCustomers : [],
        specificOrganisations: !!element.specificOrganisations ? element.specificOrganisations : [],
        specificActiveProducts: !!element.specificActiveProducts ? element.specificActiveProducts : [],
        forSpecificCustomer: !!element.specificCustomers,
        orderNr: element.orderNr,
        subscriptionDetails: element.subscriptionDetails,
        productImageId: element.productImageId,
        storeAvailability: element.storeAvailability,
        tenantDetails: !!element.tenantDetails ? element.tenantDetails : {tenant: $tenant.getTenant},
    }
}

function buildPurchasableFromAdditional(element: Additional, id: string): Purchasable {
    return {
        id: element.id!,
        name: element.name!,
        unit: element.unit,
        type: PurchaseItemType.Additional,
        description: element.description,
        pricing: element.pricing,
        price: element.price,
        thresholdPrices: element.thresholdPrices,
        graduatedPrices: element.graduatedPrices,
        enterprisePrice: element.enterprisePrice,
        parentProductId: id,
        fixedQuantity: element.fixedQuantity,
        orderNr: element.orderNr,
        additionalType: element.type,
        productImageId: element.productImageId,
        storeAvailability: element.storeAvailability,
    }
}

export function buildPurchasableFromProducts(data: Product[]): Purchasable[] {
    const purchasables: Purchasable[] = [];


    const newData = data.sort((a, b) => a.orderNr! - b.orderNr!);

    newData.forEach((element, index) => {
        const purchasableProduct = buildPurchasableFromProduct(element);
        purchasableProduct.productsSize = newData.length;
        purchasables.push(purchasableProduct);

        element.additionals?.forEach(additional => {
            let purchasableAdditional = buildPurchasableFromAdditional(additional, element.id!);
            purchasableAdditional.parentProductAdditionalsSize = element.additionals?.length;
            purchasables.push(purchasableAdditional);
        })
    })

    return purchasables;
}

export const graduatedPriceComparison = (price1: GraduatedPrice, price2: GraduatedPrice) => {
    if (!!price1 && !price2) {
        return false;
    }

    if (!price1 && !!price2) {
        return false;
    }

    if (!price1 && !price2) {
        return true;
    }

    return price1.price.gross === price2.price.gross && price1.amount === price2.amount;
}

export const thresholdPriceComparison = (price1: ThresholdPrice, price2: ThresholdPrice) => {
    if (!!price1 && !price2) {
        return false;
    }

    if (!price1 && !!price2) {
        return false;
    }

    if (!price1 && !price2) {
        return true;
    }

    return price1.price.gross === price2.price.gross && price1.fromAmount === price2.fromAmount && price1.toAmount === price2.toAmount;
}

export const productDetailsSetting= (productCategory: MyDbProductCategory, prodDetails: TenantProductDetails) => {
    const {$tenant, $mydb} = useNuxtApp();

    // = {tenant: $tenant.getTenant};
    if ($tenant.isMyDb){
        let productDetails: MyDbProductDetails = <MyDbProductDetails>{tenant: $tenant.getTenant};

        if (productCategory === "TEMPLATE"){
            productDetails = <MyDbProductDetails> {
                ...productDetails,
                // templateId: $mydb.allDatabaseTemplates[0].id,
                templateCanonicalIds: !!(<MyDbProductDetails>prodDetails).templateCanonicalIds ? (<MyDbProductDetails>prodDetails).templateCanonicalIds : [],

            }
        } else if (productCategory === "MONTHLY_SUBSCRIPTION" || productCategory === "YEARLY_SUBSCRIPTION") {
            productDetails = <MyDbProductDetails> {
                ...productDetails,
                numDatabases: !!(<MyDbProductDetails>prodDetails).numDatabases ? (<MyDbProductDetails>prodDetails).numDatabases : 1,
                storageCapacityInGb: !!(<MyDbProductDetails>prodDetails).storageCapacityInGb ? (<MyDbProductDetails>prodDetails).storageCapacityInGb : 1
            }
        } else {
            productDetails = <MyDbProductDetails> productDetails;
        }
        return <MyDbProductDetails>productDetails;
    }
}

export const subscriptionDetailsSetting = (isSubscriptionBasedProduct: boolean, prodCategory: ProductCategory, subDetails: SubscriptionDetails) => {
    const {$tenant, $mydb} = useNuxtApp();

    let subscriptionDetails: SubscriptionDetails = {};
    if ($tenant.isMyDb){

        if (isSubscriptionBasedProduct){
            let valUnit = prodCategory === ProductCategory.MonthlySubscription ? TimeUnit.Month : TimeUnit.Year;
            let autoRenew = prodCategory !== ProductCategory.Support;
            subscriptionDetails = {
                ...subscriptionDetails,
                validityUnit: valUnit,
                validityAmount: subDetails.validityAmount,
                autoRenewing: autoRenew,
            }
        } else {
            subscriptionDetails = {}
        }

        return subscriptionDetails;
    }
}
export const inputProductDialogTemplate = () : Purchasable => {
    const {$tenant} = useNuxtApp();
    // let purchasable = <Purchasable>{};

    return <Purchasable> {
        id: '',
        canonicalId: '',
        unit: Unit.Piece,
        name: '',
        type: PurchaseItemType.Product,
        description: '',
        productCategory: 'MONTHLY_SUBSCRIPTION',
        additionalType: AdditionalType.Check,
        pricing: "LINEAR",
        parentProductId: '',
        productType: ProductType.Single,
        fixedQuantity: true,
        forSpecificCustomer: false,
        specificCustomers: [],
        specificOrganisations: [],
        tenantDetails: {
            storageCapacityInGb: 1,
            numDatabases: 1,
            templateCanonicalIds: [],
        } as MyDbProductDetails,
        subscriptionDetails: {
            validityUnit: "MONTH",
            validityAmount: 1,
            autoRenewing: true,
        },
        price: {
            gross: 0
        },
        graduatedPrices: [{amount: 10, price: {gross: 1}}],
        thresholdPrices: [{fromAmount: 0, toAmount: 10, price: {gross: 1}}],
        enterprisePrice: {
            pricing: Pricing.Linear,
            price: {
                gross: 0
            },
            graduatedPrices: [{amount: 10, price: {gross: 1}}],
            thresholdPrices: [{fromAmount: 0, toAmount: 10, price: {gross: 1}}],
        },
    };
};

export const deepCopyPurchasable = (purchasable: Purchasable) => {
    const {$tenant} = useNuxtApp();
    // let purchasable = <Purchasable>{};
    let productDetails: MyDbProductDetails = <MyDbProductDetails>{tenant: $tenant.getTenant};
    // = {tenant: $tenant.getTenant};
    if ($tenant.isMyDb){
        productDetails = <MyDbProductDetails> {
            ...productDetails,
            numDatabases: (<MyDbProductDetails>purchasable.tenantDetails)?.numDatabases,
            storageCapacityInGb: (<MyDbProductDetails>purchasable.tenantDetails)?.storageCapacityInGb
        }
    }
    return <Purchasable> {
        id: purchasable.id,
        canonicalId: purchasable.canonicalId,
        unit: purchasable.unit,
        name: purchasable.name,
        type: purchasable.type,
        description: purchasable.description,
        productCategory: purchasable.productCategory,
        additionalType: purchasable.additionalType,
        pricing: purchasable.pricing,
        parentProductId: purchasable.parentProductId,
        productType: purchasable.productType,
        fixedQuantity: purchasable.fixedQuantity,
        forSpecificCustomer: purchasable.forSpecificCustomer,
        specificCustomers: JSON.parse(JSON.stringify(purchasable.specificCustomers)),
        specificOrganisations: JSON.parse(JSON.stringify(purchasable.specificOrganisations)),
        subscriptionDetails: {
            validityUnit: purchasable.subscriptionDetails?.validityUnit,
            validityAmount: purchasable.subscriptionDetails?.validityAmount,
            autoRenewing: purchasable.subscriptionDetails?.autoRenewing,
        },
        price: {
            gross: purchasable.price?.gross
        },
        graduatedPrices: JSON.parse(JSON.stringify(purchasable.graduatedPrices)),
        thresholdPrices: JSON.parse(JSON.stringify(purchasable.thresholdPrices)),
        enterprisePrice: {
            pricing: purchasable.enterprisePrice?.pricing,
            price: {
                gross: purchasable.enterprisePrice?.price?.gross
            },
            graduatedPrices: JSON.parse(JSON.stringify(purchasable.enterprisePrice?.graduatedPrices)),
            thresholdPrices: JSON.parse(JSON.stringify(purchasable.enterprisePrice?.thresholdPrices)),
        },
        tenantDetails: productDetails,
    };
}

export const purchasablesByCategoryForCustomer = (category: string, customerId: string) : QualifiedPurchasable[] => {
    const removedProductId : string[] = [];
    const {$productStore, $customerStore} = useNuxtApp();
    return $productStore.purchasablesByCategory(category).reduce((filtered: Purchasable[], option: Purchasable) => {
        if(!option.storeAvailability){
            removedProductId.push(option.id)
        } else {
            if (!!option.specificCustomers && option.specificCustomers.length > 0) {
                const customerIds = option.specificCustomers.map(customer => customer.id)
                if (customerIds.includes(customerId)){
                    filtered.push(option);
                } else {
                    removedProductId.push(option.id)
                }
            } else if (!!option.specificOrganisations && option.specificOrganisations.length > 0) {
                const orgaIds = option.specificOrganisations.map(orga => orga.id);
                if (orgaIds.includes($customerStore.currentCustomer.organisation?.id)){
                    if (!filtered.map(p => p.id).includes(option.id)){
                        filtered.push(option);
                    }
                } else {
                    removedProductId.push(option.id)
                }
            } else if (!!option.specificActiveProducts && option.specificActiveProducts.length > 0) {
                // always remove products that are only purchasable as Additionals
                removedProductId.push(option.id)
            } else {
                if (!!option.parentProductId){
                    if (!removedProductId.includes(option.parentProductId)){
                        filtered.push(option)
                    }
                } else {
                    filtered.push(option)
                }
            }
        }
        return filtered;
    }, []).map(purchasable => {
        return {purchasable: purchasable,
        quantity: purchasable.fixedQuantity ? 1 : 0 } as QualifiedPurchasable
    });
};

export const maxQuantity = (row: QualifiedPurchasable) : number => {
    if (row.purchasable.fixedQuantity) {
        return 1;
    }

    if(row.purchasable.pricing === "THRESHOLD"){
        const tp: ThresholdPrice = row.purchasable.thresholdPrices![row.purchasable.thresholdPrices?.length! -1];
        return tp.toAmount!;
    }
    return 9999;
}

export const calculateLinearPrice = (row: QualifiedPurchasable, customer: Customer): Price => {
    const linearPrice = !!customer.organisation?.enterpriseMode ? row.purchasable.enterprisePrice?.price : row.purchasable.price;

    if (!linearPrice) {
        return {
            net: 0,
            gross: 0,
            tax: 0
        };
    }

    const grossPrice = linearPrice.gross * row.quantity;
    const netPrice = Math.round(grossPrice * 100 / 1.19) / 100
    const tax = grossPrice - netPrice


    return {
        net: netPrice,
        gross: grossPrice,
        tax: tax
    };
}
export const calculateGraduatedPrice = (row: QualifiedPurchasable, customer: Customer): Price => {
    const graduatedPrice = !!customer.organisation?.enterpriseMode ?
      row.purchasable.enterprisePrice?.graduatedPrices?.find(el => el.amount === row.quantity) :
      row.purchasable.graduatedPrices?.find(el => el.amount === row.quantity);

    if (!graduatedPrice) {
        return {
            net: 0,
            gross: 0,
            tax: 0
        };
    }

    return graduatedPrice.price;
}

export const calculateThresholdPrice = (row: QualifiedPurchasable, customer: Customer): Price => {
    const thresholdPrice = !!customer.organisation?.enterpriseMode ?
      row.purchasable.enterprisePrice?.thresholdPrices?.find(el => el.fromAmount <= row.quantity && el.toAmount >= row.quantity)
      : row.purchasable.thresholdPrices?.find(el=> el.fromAmount <= row.quantity && el.toAmount >= row.quantity);

    if (!thresholdPrice) {
        return {
            net: 0,
            gross: 0,
            tax: 0
        };
    }

    const grossPrice = thresholdPrice.price.gross * row.quantity;
    const netPrice = Math.round(grossPrice * 100 / 1.19) / 100
    const tax = grossPrice - netPrice

    return {
        net: netPrice,
        gross: grossPrice,
        tax: tax
    };
}

export const checkBasketRoute = async () => {
    const route = useRoute();
    const token = route.query.token;
    const mode = route.query.mode;
    const status = route.query.status;
    const subscription_id = route.query.subscription_id;

    // if (!!token && mode === 'paypal') {
    //     try {
    //         await useOrderStore().updatePaypalPaymentProcess(subscription_id!.toString(), token.toString());
    //         if (status === 'success') {
    //             ElMessage.success('Der Bezahlvorgang war erfolgreich.');
    //         } else if (status === 'cancel') {
    //             ElMessage.error('Der Bezahlvorgang wurde abgebrochen.');
    //         }
    //     } catch (error) {
    //         console.log('error', error);
    //         ElMessage.error('Informationen über Ihren Kauf konnten nicht mit dem Zahlungsdienstleister abgeglichen werden, bitte wenden Sie sich an den Support.');
    //     }
    // }
}
